import { useMutation } from '@apollo/client';
import React from 'react';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import TopicIcon from '../topic/TopicIcon';
import ReorderSidebarItemsModal from './ReorderSidebarItemsModal';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const TOPICS_FRAGMENT = gql(`
  fragment ReorderTopicsModal_topics on Topic {
    id
    name
  }
`);

const PARENT_TOPIC_FRAGMENT = gql(`
  fragment ReorderTopicsModal_parentTopic on Topic {
    id
    name
    ancestors {
      id
      name
      ...TopicBreadcrumbs_topics
    }
  }
`);

const REORDER_TOPICS_MUTATION = gql(/** @complexity 4 */ `
  mutation ReorderTopics($parentId: ID, $topicIds: [ID!]!) {
    reorderTopics(parentId: $parentId, topicIds: $topicIds) {
      id
      order
    }
  }
`);

interface ReorderTopicsModalProps {
  topics: DocumentType<typeof TOPICS_FRAGMENT>[];
  parentTopic?: DocumentType<typeof PARENT_TOPIC_FRAGMENT>;
  close: () => void;
}

const ReorderTopicsModal = ({
  topics,
  parentTopic,
  close,
}: ReorderTopicsModalProps) => {
  const [reorderTopics] = useMutation(REORDER_TOPICS_MUTATION);

  const handleSubmit = async (topicIds: string[]) => {
    await reorderTopics({
      variables: { parentId: parentTopic?.id ?? undefined, topicIds },
    });
    close();
  };

  return (
    <ReorderSidebarItemsModal
      onSubmit={handleSubmit}
      headerIcon={<TopicIcon />}
      header={parentTopic?.name ?? 'All Topics'}
      headerSecondaryTop={
        <TopicBreadcrumbs topics={parentTopic?.ancestors ?? []} />
      }
      title={parentTopic ? 'Reorder subtopics' : 'Reorder All Topics'}
      subtitle={
        parentTopic
          ? 'Their position will change in the sidebar and topic pages for everyone'
          : 'Their position will change in the sidebar for everyone'
      }
      items={topics}
      close={close}
    />
  );
};

export default ReorderTopicsModal;
